import {
  Image,
  StyleSheet,
  Text,
  View,
  type ImageSourcePropType,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, fonts } from '@/constants/theme';

type Tone = 'green' | 'electric' | 'frost';

export interface AvatarProps {
  /** Initials to render when no photo is provided. Max 2 chars. */
  initials?: string;
  source?: ImageSourcePropType;
  size?: number;
  tone?: Tone;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
}

/**
 * Round avatar. Defaults to Deep Green bg + Electric Green initials, the
 * pattern shown in the handoff `mp-av` style. Use a photo via `source` for
 * profile shots; otherwise initials are derived from the caller.
 */
export function Avatar({
  initials,
  source,
  size = 40,
  tone = 'green',
  style,
  accessibilityLabel,
}: AvatarProps) {
  const palette = TONES[tone];
  const fontSize = Math.round(size * 0.36);
  const dimensions = { width: size, height: size, borderRadius: size / 2 };

  return (
    <View
      style={[styles.base, palette.container, dimensions, style]}
      accessibilityLabel={accessibilityLabel}
    >
      {source ? (
        <Image source={source} style={dimensions} resizeMode="cover" />
      ) : (
        <Text style={[styles.text, palette.text, { fontSize }]}>
          {(initials ?? '?').slice(0, 2).toUpperCase()}
        </Text>
      )}
    </View>
  );
}

const TONES: Record<Tone, { container: ViewStyle; text: { color: string } }> = {
  green: {
    container: { backgroundColor: colors.deepGreen },
    text: { color: colors.electricGreen },
  },
  electric: {
    container: { backgroundColor: colors.electricGreen },
    text: { color: colors.deepGreen },
  },
  frost: {
    container: { backgroundColor: colors.frost, borderWidth: 1, borderColor: colors.border },
    text: { color: colors.deepGreen },
  },
};

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  text: {
    fontFamily: fonts.bodyBold,
    letterSpacing: 0.3,
  },
});
